import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Skeleton, Typography } from '@mui/material'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import EmptyState from '../merchants/shared/EmptyState'
import { C, SX, TYPE } from '../merchants/shared/tokens'
import { errorText, fetchMerchants, loadAgreementsInto } from './ptlSetupApi'
import { DONE_STEP, ONBOARDING_STEPS, ROUTES } from './constants'
import { isFullySetUp, nextStepKey, stepStates, summaryKpis } from './components/MerchantStatus'
import { Banner } from './components/SetupUi'
import BookingReadinessBanner from './components/BookingReadiness'
import PtlPage from './shell/PtlPage'
import PtlWizardShell from './shell/PtlWizardShell'
import PtlDoneScreen from './shell/PtlDoneScreen'
import PricingStep, { pricingKpis, usePricing } from './steps/PricingStep'
import TatStep, { tatKpis, useTat } from './steps/TatStep'
import AllocationStep, { allocationKpis, useAllocation } from './steps/AllocationStep'

/**
 * PTL FREIGHT → PTL Merchant Setup → Onboarding → one merchant, one step (/onboarding/:merchantId/:step).
 * The step is in the route and its state comes from the merchant's data, so a step can be left and resumed and every
 * step has its own link. Step bodies: steps/PricingStep, steps/TatStep, steps/AllocationStep.
 *
 * @param mode  'onboard' (/onboarding/:merchantId/:step) or 'update' (/update/:merchantId/:step, from the Merchant Update
 *              hub): same screens, every step open, and the last step goes back to the hub instead of the Done screen.
 */
export default function PtlOnboardingStep({ mode = 'onboard' }) {
  const update = mode === 'update'
  const { merchantId, step } = useParams()
  const navigate = useNavigate()
  const [merchants, setMerchants] = useState(null)
  const [error, setError] = useState('')
  // Hooks run on every render; the coverage is only fetched while Step 1 is open.
  const pricing = usePricing(step === 'pricing' ? merchantId : null)
  const tat = useTat(step === 'tat' ? merchantId : null)
  const allocation = useAllocation(step === 'allocation' ? merchantId : null)
  const [uploading, setUploading] = useState(false)
  const [unsaved, setUnsaved] = useState(false)
  // Where the user asked to go while courier picks are unsaved; the leave dialog asks first.
  const [leave, setLeave] = useState(null)
  // An upload view or a draft belongs to one step; leaving the step (stepper, link) drops it.
  useEffect(() => { setUploading(false); setUnsaved(false); setLeave(null) }, [step, merchantId])
  // Reload or close of the tab with unsaved picks: the browser's own "leave site?" prompt.
  useEffect(() => {
    if (!unsaved) return undefined
    const warn = (e) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [unsaved])
  /** Runs a navigation away from the step, after the leave dialog when courier picks are unsaved. */
  const guard = (fn) => (unsaved ? setLeave(() => fn) : fn())

  const load = useCallback(() => {
    setError('')
    fetchMerchants().then((ms) => { setMerchants(ms); loadAgreementsInto(setMerchants) }).catch((e) => setError(errorText(e, 'Could not load PTL merchants')))
  }, [])
  useEffect(() => { load() }, [load])

  const merchant = useMemo(() => merchants?.find((m) => m.id === merchantId), [merchants, merchantId])
  const stepRoute = (id, key) => (update ? ROUTES.updateStep(id, key) : ROUTES.onboardingStep(id, key))
  const go = (key) => navigate(stepRoute(merchantId, key))

  if (error) {
    return (
      <PtlPage title='PTL Onboarding'>
        <Banner tone='danger' action={<Button size='small' onClick={load} sx={{ textTransform: 'none' }}>Retry</Button>}>{error}</Banner>
      </PtlPage>
    )
  }
  if (!merchants) {
    return (
      <Box sx={{ p: 2.5, display: 'grid', gap: 1.75 }} aria-busy='true'>
        <Skeleton variant='rounded' height={44} />
        <Skeleton variant='rounded' height={96} />
        <Skeleton variant='rounded' height={88} />
        <Skeleton variant='rounded' height={260} />
      </Box>
    )
  }
  if (!merchant) {
    return (
      <PtlPage title='PTL Onboarding'>
        <EmptyState title='Not a live PTL merchant'
          body='This merchant is not in the PTL merchant list. Use Onboard a merchant on Onboarding Health to turn on PTL for it, or pick another merchant.'
          action={<Button variant='contained' onClick={() => navigate(ROUTES.onboarding)} sx={SX.cta}>Back to Onboarding Health</Button>} />
      </PtlPage>
    )
  }

  const states = stepStates(merchant)
  // Update mode: a live merchant may change any area in any order.
  if (update) Object.values(states).forEach((st) => { st.lockedBy = null })
  const isDone = step === DONE_STEP
  const index = ONBOARDING_STEPS.findIndex((s) => s.key === step)
  if (update && (isDone || index < 0)) return <Navigate replace to={ROUTES.updateHub(merchant.id)} />
  if (!isDone && index < 0) return <Navigate replace to={ROUTES.onboardingStep(merchant.id, nextStepKey(merchant, ONBOARDING_STEPS) || DONE_STEP)} />
  // The Done screen only exists for a merchant that is fully set up.
  if (isDone && !isFullySetUp(merchant)) return <Navigate replace to={ROUTES.onboardingStep(merchant.id, nextStepKey(merchant, ONBOARDING_STEPS))} />

  const lockedBy = !isDone && !states[step].done ? states[step].lockedBy : null
  const last = index === ONBOARDING_STEPS.length - 1
  const footer = isDone || uploading ? null : footerFor(step, merchant, unsaved, update, {
    onBack: () => guard(() => go(ONBOARDING_STEPS[index - 1].key)),
    onNext: () => (last ? (update ? navigate(ROUTES.updateHub(merchant.id)) : go(DONE_STEP)) : go(ONBOARDING_STEPS[index + 1].key)),
    nextLabel: last ? (update ? `Back to ${merchant.name}` : 'Finish set-up') : `Next: Step ${index + 2} · ${ONBOARDING_STEPS[index + 1].label}`,
  })

  return (
    <PtlWizardShell mode={mode} merchant={merchant} stepKey={step} states={states} onGoStep={(key) => guard(() => go(key))} guard={guard} footer={footer}
      kpis={stepKpis(step, merchant, pricing, tat, allocation)}
      kpisLoading={(step === 'pricing' && !pricing.coverage && !pricing.error) || (step === 'tat' && !tat.grid && !tat.error)
        || (step === 'allocation' && !allocation.data && !allocation.error)}>
      {isDone && <PtlDoneScreen merchant={merchant} onChanged={load} />}
      {!isDone && !uploading && <BookingReadinessBanner merchant={merchant} onChanged={load} />}
      {lockedBy && (
        <EmptyState dense title={`Step ${index + 1} opens after Step 1`} body={lockedBy}
          action={<Button variant='contained' onClick={() => go(ONBOARDING_STEPS[0].key)} sx={SX.cta}>Go to Step 1 · Pricing</Button>} />
      )}
      {!lockedBy && step === 'pricing' && <PricingStep key={merchant.id} merchant={merchant} {...pricing} onChanged={load} onUploadView={setUploading} />}
      {!lockedBy && step === 'tat' && <TatStep key={merchant.id} merchant={merchant} {...tat} onChanged={load} onUploadView={setUploading} />}
      {!lockedBy && step === 'allocation' && <AllocationStep key={merchant.id} merchant={merchant} {...allocation} onChanged={load} onUnsaved={setUnsaved} />}
      <Dialog open={!!leave} onClose={() => setLeave(null)} maxWidth='xs' fullWidth>
        <DialogTitle sx={{ ...TYPE.sectionTitle, fontSize: 16 }}>Leave without saving the courier changes?</DialogTitle>
        <DialogContent>
          <Typography sx={{ ...TYPE.rowHelp, fontSize: 13.5, color: C.inkBody }}>
            The couriers picked on this step are not saved yet. Leaving drops them, and {merchant.name} keeps its current allocation couriers.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { const fn = leave; setLeave(null); setUnsaved(false); fn() }} sx={{ textTransform: 'none', fontWeight: 600, color: C.danger }}>
            Leave without saving
          </Button>
          <Button variant='contained' autoFocus onClick={() => setLeave(null)} sx={SX.cta}>Stay on this step</Button>
        </DialogActions>
      </Dialog>
    </PtlWizardShell>
  )
}

/** KPI cards of the open step; the Done screen shows the merchant summary. */
function stepKpis(step, merchant, pricing, tat, allocation) {
  if (step === 'pricing') return pricingKpis(pricing.coverage)
  if (step === 'tat') return tatKpis(tat.grid)
  if (step === 'allocation') return allocationKpis(allocation.data)
  return summaryKpis(merchant)
}

/** Next is blocked until the step's data is there; the hint says why (D2C footer pattern). Update mode only guards unsaved work. */
function footerFor(step, merchant, unsaved, update, base) {
  const s = stepStates(merchant)
  if (unsaved) return { ...base, nextDisabled: true, hint: 'Save or undo the courier changes first' }
  if (update) return base
  if (step === 'pricing' && !s.pricing.done) return { ...base, nextDisabled: true, hint: 'Upload at least one rate card to continue' }
  if (step === 'allocation' && !isFullySetUp(merchant)) return { ...base, nextDisabled: true, hint: 'Needs a rate card from Step 1 to finish' }
  return base
}
